/**
 * Route map. Two portals share one bundle:
 *   /sme/*  subject-matter experts (dashboard, guide, new/edit draft, task detail)
 *   /eng/*  engineers (dashboard, all tasks, task review, SMEs, docs)
 */
import type { ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { RequireReviewer } from './auth/RequireReviewer'
import { RequireRole } from './auth/RequireRole'
import { Layout } from './components/Layout'
import { ThemeProvider } from './components/ThemeProvider'
import Landing from './pages/Landing'
import AuthPage from './pages/AuthPage'
import SmeDashboard from './pages/sme/Dashboard'
import { TaskDirectory, DirectoryTask } from './pages/sme/TaskDirectory'
import SmeGuide from './pages/sme/Guide'
import SmeSubmit from './pages/sme/Submit'
import SmeTaskDetail from './pages/sme/TaskDetail'
import SmeReviews from './pages/sme/Reviews'
import SmeRubricTemplates, { RubricTemplateEditPage } from './pages/sme/RubricTemplates'
import SmeReview from './pages/sme/Review'
import SmeProfile from './pages/sme/Profile'
import IntroductionExample from './pages/sme/IntroductionExample'
import EngDashboard from './pages/eng/Dashboard'
import EngTasks from './pages/eng/Tasks'
import EngTaskReview from './pages/eng/TaskReview'
import EngSmes from './pages/eng/Smes'
import EngDocs from './pages/eng/Docs'
import EngRuns from './pages/eng/Runs'
import EngNewRun from './pages/eng/NewRun'
import EngRunDetail from './pages/eng/RunDetail'
import EngTaskRuns from './pages/eng/TaskRuns'
import EngAgents from './pages/eng/Agents'
import EngRemi from './pages/eng/Remi'
import TemplateIntake from './pages/scientific/TemplateIntake'

/**
 * Engineer pages. Also used to send engineers who open an /sme link to the /eng page at the same
 * path, so keep a path here only if it shows the same thing as its /sme counterpart (if any).
 */
const engRoutes: { path: string, element: ReactNode }[] = [
  { path: '/eng', element: <EngDashboard /> },
  { path: '/eng/templates/:templateId', element: <TemplateIntake developer /> },
  { path: '/eng/tasks', element: <EngTasks /> },
  { path: '/eng/tasks/:taskId', element: <EngTaskReview /> },
  { path: '/eng/runs', element: <EngRuns /> },
  { path: '/eng/runs/new', element: <EngNewRun /> },
  { path: '/eng/runs/tasks/:taskId', element: <EngTaskRuns /> },
  { path: '/eng/runs/:runId', element: <EngRunDetail /> },
  { path: '/eng/agents', element: <EngAgents /> },
  { path: '/eng/agents/messages', element: <EngAgents /> },
  { path: '/eng/agent-messages', element: <Navigate to="/eng/agents/messages" replace /> },
  // The detail is a panel over the board, but it keeps a URL so it can be linked.
  { path: '/eng/agents/:taskId/:jobId', element: <EngAgents /> },
  { path: '/eng/remi', element: <EngRemi /> },
  { path: '/eng/smes', element: <EngSmes /> },
  { path: '/eng/requests', element: <Navigate to="/eng/tasks" replace /> },
  { path: '/eng/docs', element: <EngDocs /> },
]

export default function App() {
  return (
    <ThemeProvider><Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Landing />} />

      <Route path="/sme/login" element={<Landing />} />
      <Route path="/sme/signup" element={<AuthPage role="sme" mode="signup" />} />
      <Route path="/eng/login" element={<Landing />} />
      <Route path="/eng/signup" element={<AuthPage role="engineer" mode="signup" />} />

      <Route element={<RequireRole role="sme" engineerPaths={engRoutes.map(r => r.path)} />}>
        <Route element={<Layout role="sme" />}>
          <Route path="/sme" element={<SmeDashboard />} />
          <Route path="/sme/task-directory" element={<TaskDirectory />} />
          <Route path="/sme/task-directory/:taskId" element={<DirectoryTask />} />
          <Route element={<RequireReviewer />}>
            <Route path="/sme/reviews" element={<SmeReviews />} />
            <Route path="/sme/reviews/:taskId/:roundId" element={<SmeReview />} />
          </Route>
          <Route path="/sme/guide" element={<SmeGuide />} />
          <Route path="/sme/profile" element={<SmeProfile />} />
          <Route path="/sme/introduction/example" element={<IntroductionExample />} />
          <Route path="/sme/templates/:templateId" element={<TemplateIntake />} />
          <Route path="/sme/submit" element={<SmeSubmit />} />
          <Route path="/sme/rubric-templates" element={<SmeRubricTemplates />} />
          <Route path="/sme/rubric-templates/new" element={<RubricTemplateEditPage />} />
          <Route path="/sme/rubric-templates/:templateId" element={<RubricTemplateEditPage />} />
          <Route path="/sme/tasks/:taskId" element={<SmeTaskDetail />} />
          <Route path="/sme/tasks/:taskId/edit" element={<SmeSubmit />} />
        </Route>
      </Route>

      <Route element={<RequireRole role="engineer" />}>
        <Route element={<Layout role="engineer" />}>
          {engRoutes.map(({ path, element }) => <Route key={path} path={path} element={element} />)}
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes></ThemeProvider>
  )
}
